import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Grid,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useUploadWizard } from '../../../contexts/UploadWizardContext';
import { TagMetadata } from '../../../types/upload';
import { getMeantToPlatforms } from '../../../types';
import { CONTENT_SHOWN_OPTIONS } from '../../../constants/drawContentOptions';
import LicenseSelector from '../../common/LicenseSelector';
import { getFrontendAppDeployment } from '../../../utils/appDeployment';

const AUDIENCE_OPTIONS = ['General (Kids)', 'Parents', '6 and Under', '10–12', 'Boys', 'Girls'];
const SEASONAL_OPTIONS = ['Christmas', 'Thanksgiving', 'Halloween', 'Easter', 'Mother’s Day', 'Summer', 'Valentine’s Day', 'Winter', 'New Year', 'Back to School'];

const createDefaultTagMetadata = (): TagMetadata => ({
  licensedSong: [],
  licensedSongIds: [],
  cast: 'unknown' as const,
  heroAge: 'unknown' as const,
  heroGender: 'unknown' as const,
  meantTo: [],
  audience: [],
  seasonal: [],
  contentShownInApp: [],
});

const TagStep: React.FC = () => {
  const { getCreativeGroups, setTagMetadata, getTagMetadata } = useUploadWizard();
  const creativeGroups = getCreativeGroups();
  const [expandedPanels, setExpandedPanels] = useState<Set<number>>(new Set());
  const didInitializeExpandedPanels = useRef(false);

  // Auto-initialize tag metadata when creative groups are created
  useEffect(() => {
    creativeGroups.forEach((group, creativeNumber) => {
      const existingTags = getTagMetadata(creativeNumber);
      if (!existingTags) {
        setTagMetadata(creativeNumber, createDefaultTagMetadata());
      }
    });
  }, [creativeGroups, getTagMetadata, setTagMetadata]);

  // Show every creative by default once so optional fields are reachable after auto-grouping.
  useEffect(() => {
    if (creativeGroups.size > 0 && !didInitializeExpandedPanels.current) {
      didInitializeExpandedPanels.current = true;
      setExpandedPanels(new Set(Array.from(creativeGroups.keys())));
    }
  }, [creativeGroups]);

  const handlePanelChange = (creativeNumber: number) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    const newExpanded = new Set(expandedPanels);
    if (isExpanded) {
      newExpanded.add(creativeNumber);
    } else {
      newExpanded.delete(creativeNumber);
    }
    setExpandedPanels(newExpanded);
  };

  const handleTagChange = (
    creativeNumber: number,
    field: keyof TagMetadata,
    value: string | string[]
  ) => {
    const currentTags = getTagMetadata(creativeNumber) || createDefaultTagMetadata();

    const updatedTags: TagMetadata = {
      ...currentTags,
      [field]: value
    };

    setTagMetadata(creativeNumber, updatedTags);
  };

  const handleLicenseChange = (creativeNumber: number, licenseIds: string[]) => {
    const currentTags = getTagMetadata(creativeNumber) || createDefaultTagMetadata();

    setTagMetadata(creativeNumber, {
      ...currentTags,
      licensedSongIds: licenseIds,
    });
  };

  const isCreativeComplete = (creativeNumber: number): boolean => {
    const tagData = getTagMetadata(creativeNumber);
    // Tag step is always complete -- licenses are optional
    return !!tagData;
  };

  const getCompletionStatus = () => {
    const totalCreatives = creativeGroups.size;
    const completedCreatives = Array.from(creativeGroups.keys()).filter(isCreativeComplete).length;
    return { total: totalCreatives, completed: completedCreatives };
  };

  const completionStatus = getCompletionStatus();

  if (creativeGroups.size === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning">
          No creative groups found. Please complete the previous steps first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Tag Creative Assets
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {getFrontendAppDeployment() === 'sing'
            ? 'Add metadata to help with organization and search.'
            : 'Add metadata to help with organization and search. Cast, Hero Age, and Hero Gender will be automatically detected after upload.'}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip 
            label={`${completionStatus.completed}/${completionStatus.total} Creatives Tagged`}
            color={completionStatus.completed === completionStatus.total ? 'success' : 'default'}
            variant={completionStatus.completed === completionStatus.total ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        {Array.from(creativeGroups.entries()).map(([creativeNumber, group]) => {
          const tagData = getTagMetadata(creativeNumber) || createDefaultTagMetadata();
          
          const isComplete = isCreativeComplete(creativeNumber);
          const isExpanded = expandedPanels.has(creativeNumber);
          const showDrawTagFields = getFrontendAppDeployment() === 'draw';

          return (
            <Accordion
              key={creativeNumber}
              expanded={isExpanded}
              onChange={handlePanelChange(creativeNumber)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: isComplete ? 'success.light' : 'grey.50',
                  '&:hover': {
                    backgroundColor: isComplete ? 'success.light' : 'grey.100',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                  {isComplete ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" />
                  )}
                  <Box>
                    <Typography variant="h6">
                      Creative {creativeNumber} - {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.files.length} file(s) • Ratios: {group.ratios.join(', ')}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={3}>
                  <>
                  <Grid item xs={12}>
                    <LicenseSelector
                      licenseType="song"
                      selectedLicenseIds={tagData.licensedSongIds || []}
                      onSelectionChange={(ids) => handleLicenseChange(creativeNumber, ids)}
                      label="Song Licenses"
                      placeholder="Select song licenses used in this creative..."
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Select licensed songs used in this creative. Leave empty if no licensed songs are used.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={getMeantToPlatforms()}
                      value={tagData.meantTo || []}
                      onChange={(event, newValue) => {
                        handleTagChange(creativeNumber, 'meantTo', newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={option}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Meant To"
                          placeholder={tagData.meantTo?.length ? "Add another platform..." : "Select platforms this ad is meant for"}
                          helperText="Select the platforms this ad is intended for. You can select multiple platforms."
                        />
                      )}
                    />
                  </Grid>


                  {showDrawTagFields && (
                    <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Audience
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={AUDIENCE_OPTIONS}
                      value={tagData.audience || []}
                      onChange={(_event, newValue) => {
                        handleTagChange(creativeNumber, 'audience', newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Audience" placeholder={tagData.audience?.length ? "Add audience..." : "Select audience tags"} />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={SEASONAL_OPTIONS}
                      value={tagData.seasonal || []}
                      onChange={(_event, newValue) => {
                        handleTagChange(creativeNumber, 'seasonal', newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Seasonal" placeholder={tagData.seasonal?.length ? "Add season..." : "Select seasonal tags"} />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Draw
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      fullWidth
                      freeSolo
                      options={CONTENT_SHOWN_OPTIONS}
                      value={tagData.contentShownInApp || []}
                      onChange={(_event, newValue) => {
                        handleTagChange(creativeNumber, 'contentShownInApp', newValue);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Draw Content"
                          placeholder={tagData.contentShownInApp?.length ? "Add another Draw content tag..." : "Select or type Draw content"}
                          helperText="Relevant Draw content shown in the app. You can select from the word bank or add free-text tags."
                        />
                      )}
                    />
                  </Grid>

                    </>
                  )}

                  </>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {completionStatus.completed < completionStatus.total && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Please complete the tag information for all creative groups to continue to the next step.
        </Alert>
      )}
    </Box>
  );
};

export default TagStep;